import React, { useState, useEffect, useCallback } from 'react';
import {
  HiOutlineDocumentReport, HiOutlineDownload, HiOutlineChevronDown,
  HiOutlineCog, HiOutlineTrash, HiOutlineSave, HiOutlinePlus, HiOutlineX,
  HiOutlineClock, HiOutlineEye, HiOutlineEyeOff, HiOutlineRefresh, HiOutlinePencil,
} from 'react-icons/hi';
import {
  getAllAuditConfigs, saveAuditConfig, deleteAuditConfig,
  generateReport, listAuditReports, getAuditReport, deleteAuditReport,
  downloadReportExcel, getCronStatus, triggerCronReport,
} from '../services/api';
import { toast } from 'react-toastify';
import './GcpReport.css';

const EMPTY_VM = { vmId: '', vmZone: '', device: '/dev/sda1' };
const EMPTY_BUCKET = { bucketName: '' };
const EMPTY_DISK = { diskName: '' };

function defaultConfigForm(projectId = '') {
  return {
    projectId,
    vms: [{ ...EMPTY_VM }],
    buckets: [{ ...EMPTY_BUCKET }],
    disks: [{ ...EMPTY_DISK }],
    alertPolicies: {},
  };
}

function configFromApi(cfg) {
  const vms = cfg.vms?.length
    ? cfg.vms.map(v => ({ ...EMPTY_VM, ...v }))
    : (cfg.vmId ? [{ vmId: cfg.vmId, vmZone: cfg.vmZone || '', device: cfg.device || '/dev/sda1' }] : [{ ...EMPTY_VM }]);
  const buckets = cfg.buckets?.length
    ? cfg.buckets.map(b => ({ ...EMPTY_BUCKET, ...b }))
    : (cfg.bucketName ? [{ bucketName: cfg.bucketName }] : [{ ...EMPTY_BUCKET }]);
  const disks = cfg.disks?.length
    ? cfg.disks.map(d => ({ ...EMPTY_DISK, ...d }))
    : (cfg.diskName ? [{ diskName: cfg.diskName }] : [{ ...EMPTY_DISK }]);
  return {
    projectId: cfg.gcpProjectId,
    vms,
    buckets,
    disks,
    alertPolicies: cfg.alertPolicies || {},
  };
}

function getReportVms(proj) {
  if (proj.vms?.length) return proj.vms;
  if (proj.vmId && proj.vmZone) {
    return [{
      vmId: proj.vmId,
      vmZone: proj.vmZone,
      device: proj.device || '/dev/sda1',
      vmName: proj.vmName || 'N/A',
      disk: proj.disk || { max: 0, min: 0, avg: 0, current: 0 },
    }];
  }
  if (proj.vmName && proj.vmName !== 'N/A') {
    return [{
      vmName: proj.vmName,
      device: proj.device || '/dev/sda1',
      disk: proj.disk || { max: 0, min: 0, avg: 0, current: 0 },
    }];
  }
  return [];
}

function getReportBuckets(proj) {
  if (proj.buckets?.length) return proj.buckets;
  if (proj.bucketName) {
    return [{
      bucketName: proj.bucketName,
      bucketWeekly: proj.bucketWeekly || 0,
      bucket24h: proj.bucket24h || 0,
    }];
  }
  return [];
}

function getReportDisks(proj) {
  if (proj.disks?.length) return proj.disks;
  if (proj.diskName) {
    return [{
      diskName: proj.diskName,
      snapshots24h: proj.snapshots24h || { count: 0, names: [] },
      snapshotsWeekly: proj.snapshotsWeekly || { count: 0, names: [] },
      snapshotsMonthly: proj.snapshotsMonthly || { count: 0, names: [] },
    }];
  }
  return [];
}

function GcpAudit({ gcpProjects }) {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const [generating, setGenerating] = useState(false);
  const [reports, setReports] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState({});

  const [savedReports, setSavedReports] = useState([]);
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [expandedReportData, setExpandedReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const [activeTab, setActiveTab] = useState('generate');

  const [cronStatus, setCronStatus] = useState(null);
  const [triggeringCron, setTriggeringCron] = useState(false);

  // Config management
  const [configs, setConfigs] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configForm, setConfigForm] = useState(defaultConfigForm());
  const [savingConfig, setSavingConfig] = useState(false);
  const [newPolicyName, setNewPolicyName] = useState('');
  const [newPolicyDesc, setNewPolicyDesc] = useState('');
  const [newPolicySeverity, setNewPolicySeverity] = useState('ERROR');

  const loadConfigs = useCallback(async () => {
    try {
      const res = await getAllAuditConfigs();
      setConfigs(res.data || []);
    } catch { setConfigs([]); }
  }, []);

  const loadSavedReports = useCallback(async () => {
    try {
      const res = await listAuditReports();
      setSavedReports(res.data || []);
    } catch { setSavedReports([]); }
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);
  useEffect(() => { loadSavedReports(); }, [loadSavedReports]);
  useEffect(() => {
    getCronStatus().then(res => setCronStatus(res.data)).catch(() => {});
  }, []);

  // ── Generate ──
  const handleGenerate = async () => {
    setGenerating(true);
    setReports(null);
    try {
      const res = await generateReport(startDate, endDate);
      setReports(res.data);
      const expanded = {};
      for (const r of (res.data.reports || [])) expanded[r.projectId || r.gcpProjectId] = true;
      setExpandedProjects(expanded);
      toast.success(`Audit generated for ${res.data.reports?.length || 0} project(s)`);
      loadSavedReports();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.error || err.message));
    }
    setGenerating(false);
  };

  // ── Excel download ──
  const handleDownloadExcel = async (reportData) => {
    const data = reportData || reports;
    if (!data?.reports?.length) return;
    setDownloading(true);
    try {
      const res = await downloadReportExcel(data.reports, data.startDate, data.endDate);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `GCP_Audit_${data.startDate}_to_${data.endDate}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed: ' + (err.response?.data?.error || err.message));
    }
    setDownloading(false);
  };

  // ── View / Hide saved report inline ──
  const handleToggleReport = async (report) => {
    if (expandedReportId === report._id) {
      setExpandedReportId(null);
      setExpandedReportData(null);
      return;
    }
    setLoadingReport(true);
    setExpandedReportId(report._id);
    try {
      const res = await getAuditReport(report._id);
      setExpandedReportData(res.data);
    } catch {
      toast.error('Failed to load report');
      setExpandedReportId(null);
    }
    setLoadingReport(false);
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Delete this saved report?')) return;
    try {
      await deleteAuditReport(reportId);
      toast.success('Report deleted');
      loadSavedReports();
      if (expandedReportId === reportId) { setExpandedReportId(null); setExpandedReportData(null); }
    } catch { toast.error('Failed to delete report'); }
  };

  // ── Cron ──
  const handleTriggerCron = async (type) => {
    if (!window.confirm(`Manually trigger ${type} audit for ALL configured projects?`)) return;
    setTriggeringCron(true);
    try {
      const res = await triggerCronReport(type);
      if (res.data.skipped) {
        toast.warn('A cron job is already running. Try again later.');
      } else {
        toast.success(`${type} audit completed — ${res.data.projects?.length || 0} projects, ${res.data.errors?.length || 0} errors`);
        loadSavedReports();
      }
    } catch (err) {
      toast.error('Trigger failed: ' + (err.response?.data?.error || err.message));
    }
    setTriggeringCron(false);
    getCronStatus().then(res => setCronStatus(res.data)).catch(() => {});
  };

  // ── Config management ──
  const unconfiguredProjects = gcpProjects.filter(p => !configs.some(c => c.gcpProjectId === p._id));

  const handleEditConfig = (cfg) => {
    setEditingConfig(cfg);
    setConfigForm(configFromApi(cfg));
  };

  const handleNewConfig = () => {
    if (!unconfiguredProjects.length) { toast.warn('All projects are already configured'); return; }
    setEditingConfig('new');
    setConfigForm(defaultConfigForm(unconfiguredProjects[0]._id));
  };

  const updateListItem = (listKey, index, field, value) => {
    setConfigForm(prev => ({
      ...prev,
      [listKey]: prev[listKey].map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const addListItem = (listKey, emptyItem) => {
    setConfigForm(prev => ({ ...prev, [listKey]: [...prev[listKey], { ...emptyItem }] }));
  };

  const removeListItem = (listKey, index, emptyItem) => {
    setConfigForm(prev => ({
      ...prev,
      [listKey]: prev[listKey].length === 1
        ? [{ ...emptyItem }]
        : prev[listKey].filter((_, i) => i !== index),
    }));
  };

  const handleSaveConfig = async () => {
    if (!configForm.projectId) { toast.warn('Select a project'); return; }
    setSavingConfig(true);
    try {
      await saveAuditConfig(configForm.projectId, configForm);
      toast.success('Config saved');
      setEditingConfig(null);
      loadConfigs();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.error || err.message));
    }
    setSavingConfig(false);
  };

  const handleDeleteConfig = async (cfg) => {
    if (!window.confirm(`Delete audit config for ${cfg.projectName}?`)) return;
    try {
      await deleteAuditConfig(cfg.gcpProjectId);
      toast.success('Config deleted');
      loadConfigs();
    } catch { toast.error('Failed to delete config'); }
  };

  const handleAddPolicy = () => {
    if (!newPolicyName.trim()) return;
    setConfigForm(prev => ({
      ...prev,
      alertPolicies: {
        ...prev.alertPolicies,
        [newPolicyName.trim()]: { description: newPolicyDesc, severity: newPolicySeverity },
      },
    }));
    setNewPolicyName(''); setNewPolicyDesc(''); setNewPolicySeverity('ERROR');
  };

  const handleRemovePolicy = (name) => {
    setConfigForm(prev => {
      const updated = { ...prev.alertPolicies };
      delete updated[name];
      return { ...prev, alertPolicies: updated };
    });
  };

  const toggleProject = (id) => setExpandedProjects(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="gcp-report">
      <div className="pv-header">
        <div className="pv-header-left">
          <h1><HiOutlineDocumentReport /> GCP Audit</h1>
          <p className="pv-subtitle">
            Audit all configured projects — VM, bucket, snapshots, alerts &amp; job failures
          </p>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="audit-tabs">
        <button className={`audit-tab ${activeTab === 'generate' ? 'active' : ''}`} onClick={() => setActiveTab('generate')}>
          <HiOutlineDocumentReport /> Generate Report
        </button>
        <button className={`audit-tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
          <HiOutlineClock /> Saved Reports ({savedReports.length})
        </button>
        <button className={`audit-tab ${activeTab === 'schedule' ? 'active' : ''}`} onClick={() => setActiveTab('schedule')}>
          <HiOutlineRefresh /> Schedule
        </button>
        <button className={`audit-tab ${activeTab === 'config' ? 'active' : ''}`} onClick={() => setActiveTab('config')}>
          <HiOutlineCog /> Configuration ({configs.length})
        </button>
      </div>

      {/* ── Generate Tab ── */}
      {activeTab === 'generate' && (
        <>
          {configs.length === 0 && (
            <div className="report-note warning" style={{ marginBottom: 16 }}>
              No project configs found. Go to the <strong>Configuration</strong> tab to add project audit settings.
            </div>
          )}
          <div className="report-action-bar">
            <div className="config-field">
              <label>Start Date</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
            <div className="config-field">
              <label>End Date</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>
            <button className="generate-btn" onClick={handleGenerate} disabled={generating || configs.length === 0}>
              <HiOutlineDocumentReport />
              {generating ? 'Generating...' : `Generate Audit (${configs.length} projects)`}
            </button>
            {reports?.reports?.some(r => !r.error) && (
              <button className="download-btn" onClick={() => handleDownloadExcel()} disabled={downloading}>
                <HiOutlineDownload /> {downloading ? 'Downloading...' : 'Download Excel'}
              </button>
            )}
          </div>

          {generating && (
            <div className="report-progress">
              <div className="loading-bar"><div className="loading-bar-inner" /></div>
              <div className="report-progress-text">Fetching metrics for all configured projects from GCP…</div>
            </div>
          )}

          {reports && !generating && <ReportView data={reports} expandedProjects={expandedProjects} toggleProject={toggleProject} />}
        </>
      )}

      {/* ── History Tab (Saved Reports with inline View/Hide) ── */}
      {activeTab === 'history' && (
        <div className="saved-reports-section">
          {savedReports.length === 0 ? (
            <div className="empty-state" style={{ padding: 40 }}>No saved reports yet. Generate a report first.</div>
          ) : (
            <div className="saved-reports-list">
              {savedReports.map(r => {
                const isExpanded = expandedReportId === r._id;
                return (
                  <div key={r._id} className={`saved-report-card ${isExpanded ? 'active' : ''}`}>
                    <div className="saved-report-row">
                      <div className="saved-report-info">
                        <div className="saved-report-dates">
                          {r.startDate} → {r.endDate}
                          <span className={`report-type-badge ${r.reportType || 'manual'}`}>{r.reportType || 'manual'}</span>
                        </div>
                        <div className="saved-report-time">
                          {new Date(r.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST
                        </div>
                      </div>
                      <div className="saved-report-actions">
                        <button className="view-report-btn" onClick={() => handleToggleReport(r)} disabled={loadingReport && expandedReportId === r._id}>
                          {isExpanded ? <><HiOutlineEyeOff /> Hide</> : <><HiOutlineEye /> View</>}
                        </button>
                        {isExpanded && expandedReportData && (
                          <button className="download-btn" style={{ padding: '5px 12px', fontSize: '0.78rem' }}
                            onClick={() => handleDownloadExcel(expandedReportData.reportData)}
                            disabled={downloading}>
                            <HiOutlineDownload /> Excel
                          </button>
                        )}
                        <button className="delete-report-btn" onClick={() => handleDeleteReport(r._id)}>
                          <HiOutlineTrash />
                        </button>
                      </div>
                    </div>

                    {/* Inline expanded report */}
                    {isExpanded && (
                      <div className="saved-report-expanded">
                        {loadingReport ? (
                          <div className="report-progress" style={{ margin: '12px 0' }}>
                            <div className="loading-bar"><div className="loading-bar-inner" /></div>
                            <div className="report-progress-text">Loading report…</div>
                          </div>
                        ) : expandedReportData ? (
                          <ReportView
                            data={expandedReportData.reportData}
                            expandedProjects={expandedProjects}
                            toggleProject={toggleProject}
                          />
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Schedule Tab ── */}
      {activeTab === 'schedule' && (
        <div className="schedule-section">
          <div className="schedule-info-card">
            <h3><HiOutlineRefresh /> Scheduled Audit Reports</h3>
            <p className="schedule-desc">
              Audit reports are automatically generated for all projects with configured audit settings.
            </p>
            <div className="schedule-grid">
              <div className="schedule-item">
                <div className="schedule-label">Weekly Report</div>
                <div className="schedule-value">Every Monday at 12:00 PM IST</div>
                <div className="schedule-sub">Covers last 7 days</div>
                {cronStatus?.weeklyActive && <span className="status-badge success" style={{ marginTop: 4 }}>Active</span>}
                {cronStatus?.lastWeeklyRun && (
                  <div className="schedule-last-run">
                    Last run: {new Date(cronStatus.lastWeeklyRun.completedAt || cronStatus.lastWeeklyRun.startedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST
                    — {cronStatus.lastWeeklyRun.projects?.length || 0} projects, {cronStatus.lastWeeklyRun.errors?.length || 0} errors
                  </div>
                )}
              </div>
              <div className="schedule-item">
                <div className="schedule-label">Monthly Report</div>
                <div className="schedule-value">Every 1st of month at 12:00 PM IST</div>
                <div className="schedule-sub">Covers last 30 days</div>
                {cronStatus?.monthlyActive && <span className="status-badge success" style={{ marginTop: 4 }}>Active</span>}
                {cronStatus?.lastMonthlyRun && (
                  <div className="schedule-last-run">
                    Last run: {new Date(cronStatus.lastMonthlyRun.completedAt || cronStatus.lastMonthlyRun.startedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST
                    — {cronStatus.lastMonthlyRun.projects?.length || 0} projects, {cronStatus.lastMonthlyRun.errors?.length || 0} errors
                  </div>
                )}
              </div>
            </div>
            <div className="schedule-actions">
              <span className="schedule-actions-label">Manual Trigger:</span>
              <button className="generate-btn" onClick={() => handleTriggerCron('weekly')} disabled={triggeringCron || cronStatus?.running}>
                <HiOutlineRefresh /> {triggeringCron ? 'Running...' : 'Run Weekly Now'}
              </button>
              <button className="generate-btn" onClick={() => handleTriggerCron('monthly')} disabled={triggeringCron || cronStatus?.running} style={{ background: '#6366f1' }}>
                <HiOutlineRefresh /> {triggeringCron ? 'Running...' : 'Run Monthly Now'}
              </button>
              {cronStatus?.running && <span className="status-badge warning">Cron job is currently running...</span>}
            </div>
          </div>
        </div>
      )}

      {/* ── Configuration Tab ── */}
      {activeTab === 'config' && (
        <div className="config-page">
          <div className="config-page-header">
            <h3>Project Audit Configurations</h3>
            <button className="generate-btn" onClick={handleNewConfig} disabled={unconfiguredProjects.length === 0}>
              <HiOutlinePlus /> Add Project Config
            </button>
          </div>

          {/* Config editor form */}
          {editingConfig && (
            <div className="report-config-panel" style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>{editingConfig === 'new' ? 'Add New Config' : `Edit — ${editingConfig.projectName}`}</h3>
                <button className="remove-policy-btn" onClick={() => setEditingConfig(null)} title="Cancel"><HiOutlineX size={20} /></button>
              </div>

              {editingConfig === 'new' && (
                <div className="config-field" style={{ marginBottom: 14 }}>
                  <label>Select GCP Project</label>
                  <select value={configForm.projectId} onChange={e => setConfigForm(prev => ({ ...prev, projectId: e.target.value }))}>
                    {unconfiguredProjects.map(p => (
                      <option key={p._id} value={p._id}>{p.projectName} ({p.projectId})</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="config-resource-section">
                <div className="config-resource-header">
                  <h4>VM Instances</h4>
                  <button type="button" className="add-policy-btn" onClick={() => addListItem('vms', EMPTY_VM)}>
                    <HiOutlinePlus /> Add VM
                  </button>
                </div>
                {configForm.vms.map((vm, idx) => (
                  <div key={`vm-${idx}`} className="config-resource-row">
                    <div className="config-field">
                      <label>VM Instance ID</label>
                      <input type="text" value={vm.vmId} onChange={e => updateListItem('vms', idx, 'vmId', e.target.value)} placeholder="e.g. 4316766397061198200" />
                    </div>
                    <div className="config-field">
                      <label>VM Zone</label>
                      <input type="text" value={vm.vmZone} onChange={e => updateListItem('vms', idx, 'vmZone', e.target.value)} placeholder="e.g. us-central1-a" />
                    </div>
                    <div className="config-field">
                      <label>Disk Device</label>
                      <input type="text" value={vm.device} onChange={e => updateListItem('vms', idx, 'device', e.target.value)} placeholder="/dev/sda1" />
                    </div>
                    <button type="button" className="remove-policy-btn config-resource-remove" onClick={() => removeListItem('vms', idx, EMPTY_VM)} title="Remove VM">
                      <HiOutlineX />
                    </button>
                  </div>
                ))}
              </div>

              <div className="config-resource-section">
                <div className="config-resource-header">
                  <h4>GCS Buckets</h4>
                  <button type="button" className="add-policy-btn" onClick={() => addListItem('buckets', EMPTY_BUCKET)}>
                    <HiOutlinePlus /> Add Bucket
                  </button>
                </div>
                {configForm.buckets.map((bucket, idx) => (
                  <div key={`bucket-${idx}`} className="config-resource-row config-resource-row-single">
                    <div className="config-field">
                      <label>GCS Bucket Name</label>
                      <input type="text" value={bucket.bucketName} onChange={e => updateListItem('buckets', idx, 'bucketName', e.target.value)} placeholder="e.g. propvr_mongo_db_uat_backup" />
                    </div>
                    <button type="button" className="remove-policy-btn config-resource-remove" onClick={() => removeListItem('buckets', idx, EMPTY_BUCKET)} title="Remove Bucket">
                      <HiOutlineX />
                    </button>
                  </div>
                ))}
              </div>

              <div className="config-resource-section">
                <div className="config-resource-header">
                  <h4>Snapshot Disks</h4>
                  <button type="button" className="add-policy-btn" onClick={() => addListItem('disks', EMPTY_DISK)}>
                    <HiOutlinePlus /> Add Disk
                  </button>
                </div>
                {configForm.disks.map((disk, idx) => (
                  <div key={`disk-${idx}`} className="config-resource-row config-resource-row-single">
                    <div className="config-field">
                      <label>Snapshot Disk Name</label>
                      <input type="text" value={disk.diskName} onChange={e => updateListItem('disks', idx, 'diskName', e.target.value)} placeholder="e.g. mongodb-uat-backup-disk" />
                    </div>
                    <button type="button" className="remove-policy-btn config-resource-remove" onClick={() => removeListItem('disks', idx, EMPTY_DISK)} title="Remove Disk">
                      <HiOutlineX />
                    </button>
                  </div>
                ))}
              </div>

              <div className="alert-policies-section">
                <h4>Alert Policies to Monitor</h4>
                {Object.entries(configForm.alertPolicies).map(([name, cfg]) => (
                  <div key={name} className="alert-policy-row">
                    <input type="text" value={name} disabled />
                    <input type="text" value={typeof cfg === 'object' ? cfg.description : cfg} disabled />
                    <select value={typeof cfg === 'object' ? cfg.severity : 'INFO'} disabled>
                      <option>CRITICAL</option><option>ERROR</option><option>WARNING</option><option>INFO</option>
                    </select>
                    <button className="remove-policy-btn" onClick={() => handleRemovePolicy(name)} title="Remove"><HiOutlineX /></button>
                  </div>
                ))}
                <div className="alert-policy-row">
                  <input type="text" value={newPolicyName} onChange={e => setNewPolicyName(e.target.value)} placeholder="Policy display name" />
                  <input type="text" value={newPolicyDesc} onChange={e => setNewPolicyDesc(e.target.value)} placeholder="Description" />
                  <select value={newPolicySeverity} onChange={e => setNewPolicySeverity(e.target.value)}>
                    <option>CRITICAL</option><option>ERROR</option><option>WARNING</option><option>INFO</option>
                  </select>
                  <button className="add-policy-btn" onClick={handleAddPolicy}><HiOutlinePlus /> Add</button>
                </div>
              </div>

              <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                <button className="save-config-btn" onClick={handleSaveConfig} disabled={savingConfig}>
                  <HiOutlineSave /> {savingConfig ? 'Saving...' : 'Save Config'}
                </button>
                <button className="save-config-btn" onClick={() => setEditingConfig(null)}>Cancel</button>
              </div>
            </div>
          )}

          {/* Existing configs list */}
          {configs.length === 0 && !editingConfig ? (
            <div className="empty-state" style={{ padding: 40 }}>No audit configurations yet. Click "Add Project Config" to get started.</div>
          ) : (
            <div className="configs-list">
              {configs.map(cfg => (
                <div key={cfg._id} className="config-card">
                  <div className="config-card-header">
                    <div>
                      <div className="config-card-title">{cfg.projectName}</div>
                      <div className="config-card-sub">{cfg.projectId}</div>
                    </div>
                    <div className="config-card-actions">
                      <button className="view-report-btn" onClick={() => handleEditConfig(cfg)}><HiOutlinePencil /> Edit</button>
                      <button className="delete-report-btn" onClick={() => handleDeleteConfig(cfg)}><HiOutlineTrash /></button>
                    </div>
                  </div>
                  <div className="config-card-details">
                    <div className="config-detail"><span>VMs</span><span>{(cfg.vms?.length || (cfg.vmId ? 1 : 0))} configured</span></div>
                    <div className="config-detail"><span>Buckets</span><span>{(cfg.buckets?.length || (cfg.bucketName ? 1 : 0))} configured</span></div>
                    <div className="config-detail"><span>Snapshot Disks</span><span>{(cfg.disks?.length || (cfg.diskName ? 1 : 0))} configured</span></div>
                    <div className="config-detail"><span>Alert Policies</span><span>{Object.keys(cfg.alertPolicies || {}).length} configured</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Report View ──────────────────────────────────────────────

function ReportView({ data, expandedProjects, toggleProject }) {
  if (!data?.reports?.length) return null;

  return (
    <div className="report-results">
      {data.reports.map(proj => {
        if (proj.error) {
          return (
            <div className="report-error-card" key={proj.projectId || proj.gcpProjectId}>
              <strong>{proj.projectName || proj.projectId}</strong>: {proj.error}
            </div>
          );
        }

        const pid = proj.projectId || proj.gcpProjectId;
        const expanded = expandedProjects[pid] !== false;

        return (
          <div className="report-project-section" key={pid}>
            <div className="report-project-header" onClick={() => toggleProject(pid)}>
              <h3>{proj.projectName || proj.projectId}</h3>
              <HiOutlineChevronDown className={`chevron ${expanded ? 'expanded' : ''}`} />
            </div>
            {expanded && (
              <div className="report-project-body">
                <HealthCards proj={proj} />
                <DiskSection proj={proj} />
                <BucketSection proj={proj} />
                <SnapshotSection proj={proj} />
                <AlertSection proj={proj} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Health Summary Cards ─────────────────────────────────────

function HealthCards({ proj }) {
  const vms = getReportVms(proj);
  const buckets = getReportBuckets(proj);
  const disks = getReportDisks(proj);
  const maxDisk = vms.length ? Math.max(...vms.map(vm => vm.disk?.current || 0)) : 0;
  const diskOk = maxDisk < 80;
  const buckets24h = buckets.filter(b => (b.bucket24h || 0) > 0).length;
  const disks24h = disks.filter(d => (d.snapshots24h?.count || 0) > 0).length;
  const alertsOpen = proj.alertIncidents?.openCount || 0;
  const failures = proj.jobFailures?.length || 0;

  return (
    <div className="report-section">
      <h4>Health Summary</h4>
      <div className="health-cards">
        <div className={`health-card ${vms.length ? (diskOk ? 'healthy' : 'critical') : 'neutral'}`}>
          <div className="hc-label">Disk Usage</div>
          <div className="hc-value">{vms.length ? `${maxDisk.toFixed(1)}%` : 'N/A'}</div>
          <div className="hc-sub">{vms.length ? `${vms.length} VM${vms.length > 1 ? 's' : ''} monitored` : 'No VM configured'}</div>
        </div>
        <div className={`health-card ${buckets24h > 0 ? 'healthy' : (buckets.length ? 'warning' : 'neutral')}`}>
          <div className="hc-label">Bucket (24h)</div>
          <div className="hc-value">{buckets.length ? `${buckets24h}/${buckets.length}` : 'N/A'}</div>
          <div className="hc-sub">{buckets.length ? `${buckets.length} bucket${buckets.length > 1 ? 's' : ''} active` : 'No bucket configured'}</div>
        </div>
        <div className={`health-card ${disks24h > 0 ? 'healthy' : (disks.length ? 'warning' : 'neutral')}`}>
          <div className="hc-label">Snapshots (24h)</div>
          <div className="hc-value">{disks.length ? `${disks24h}/${disks.length}` : 'N/A'}</div>
          <div className="hc-sub">{disks.length ? `${disks.length} disk${disks.length > 1 ? 's' : ''} monitored` : 'No disk configured'}</div>
        </div>
        <div className={`health-card ${alertsOpen === 0 ? 'healthy' : 'critical'}`}>
          <div className="hc-label">Active Alerts</div>
          <div className="hc-value">{alertsOpen}</div>
          <div className="hc-sub">{proj.alertIncidents?.policiesFound || 0} policies monitored</div>
        </div>
        <div className={`health-card ${failures === 0 ? 'healthy' : 'critical'}`}>
          <div className="hc-label">Job Failures</div>
          <div className="hc-value">{failures}</div>
          <div className="hc-sub">Cloud Run · Scheduler · Tasks</div>
        </div>
      </div>
    </div>
  );
}

// ── Disk Utilization ─────────────────────────────────────────

function DiskSection({ proj }) {
  const vms = getReportVms(proj);

  if (!vms.length) {
    return (
      <div className="report-section">
        <h4>Disk Utilization</h4>
        <div className="empty-state" style={{ padding: 16 }}>No VM configured.</div>
      </div>
    );
  }

  return (
    <div className="report-section">
      <details open>
        <summary className="section-toggle">
          <h4>Disk Utilization ({vms.length} VM{vms.length > 1 ? 's' : ''})</h4>
        </summary>
        <div className="resource-table-wrap">
          <table className="resource-table">
            <thead>
              <tr><th>VM Name</th><th>Device</th><th>Current</th><th>Maximum</th><th>Minimum</th><th>Average</th><th>Status</th></tr>
            </thead>
            <tbody>
              {vms.map((vm, idx) => {
                const stats = vm.disk || { current: 0, max: 0, min: 0, avg: 0 };
                const ok = stats.current < 80;
                return (
                  <tr key={`${vm.vmName || 'vm'}-${idx}`}>
                    <td style={{ fontWeight: 600 }}>{vm.vmName || 'N/A'}</td>
                    <td>{vm.device || '/dev/sda1'}</td>
                    <td>{stats.current.toFixed(1)}%</td>
                    <td>{stats.max.toFixed(1)}%</td>
                    <td>{stats.min.toFixed(1)}%</td>
                    <td>{stats.avg.toFixed(1)}%</td>
                    <td><span className={`status-badge ${ok ? 'success' : 'error'}`}>{ok ? 'OK' : 'WARNING'}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

// ── Bucket Uploads ───────────────────────────────────────────

function BucketSection({ proj }) {
  const buckets = getReportBuckets(proj);

  if (!buckets.length) {
    return (
      <div className="report-section">
        <h4>Bucket Uploads</h4>
        <div className="empty-state" style={{ padding: 16 }}>No bucket configured.</div>
      </div>
    );
  }

  return (
    <div className="report-section">
      <details open>
        <summary className="section-toggle">
          <h4>Bucket Upload Statistics ({buckets.length})</h4>
        </summary>
        <div className="resource-table-wrap">
          <table className="resource-table">
            <thead>
              <tr><th>Bucket Name</th><th>Files (Date Range)</th><th>Files (Last 24h)</th><th>Status</th></tr>
            </thead>
            <tbody>
              {buckets.map((bucket, idx) => {
                const weekly = bucket.bucketWeekly || 0;
                const daily = bucket.bucket24h || 0;
                let status, statusClass;
                if (daily === 0 && weekly === 0) { status = 'No Data'; statusClass = 'error'; }
                else if (daily === 0 && weekly > 0) { status = 'Stale Data'; statusClass = 'warning'; }
                else { status = 'Healthy'; statusClass = 'success'; }

                return (
                  <tr key={`${bucket.bucketName}-${idx}`}>
                    <td style={{ fontWeight: 600 }}>{bucket.bucketName}</td>
                    <td>{weekly}</td>
                    <td>{daily}</td>
                    <td><span className={`status-badge ${statusClass}`}>{status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

// ── Snapshots ────────────────────────────────────────────────

function SnapshotSection({ proj }) {
  const disks = getReportDisks(proj);

  if (!disks.length) {
    return (
      <div className="report-section">
        <h4>Snapshot Statistics</h4>
        <div className="empty-state" style={{ padding: 16 }}>No snapshot disk configured.</div>
      </div>
    );
  }

  return (
    <div className="report-section">
      <details open>
        <summary className="section-toggle">
          <h4>Snapshot Statistics ({disks.length})</h4>
        </summary>
        <div className="resource-table-wrap">
          <table className="resource-table">
            <thead>
              <tr><th>Disk Name</th><th>Last 24h (IST)</th><th>Weekly</th><th>Monthly</th><th>Status</th></tr>
            </thead>
            <tbody>
              {disks.map((disk, idx) => {
                const s24 = disk.snapshots24h?.count || 0;
                const sW = disk.snapshotsWeekly?.count || 0;
                const sM = disk.snapshotsMonthly?.count || 0;
                let snapStatus, snapClass;
                if (s24 === 0 && sW === 0 && sM === 0) { snapStatus = 'No Snapshots'; snapClass = 'error'; }
                else if (s24 === 0 && sW > 0) { snapStatus = 'Stale'; snapClass = 'warning'; }
                else { snapStatus = 'Healthy'; snapClass = 'success'; }

                return (
                  <tr key={`${disk.diskName}-${idx}`}>
                    <td style={{ fontWeight: 600 }}>{disk.diskName}</td>
                    <td>{s24} snapshots</td>
                    <td>{sW} snapshots</td>
                    <td>{sM} snapshots</td>
                    <td><span className={`status-badge ${snapClass}`}>{snapStatus}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

// ── Alert Policies & Incidents (combined with Job Failures) ──

function AlertSection({ proj }) {
  const alertData = proj.alertIncidents || {};
  const policyWise = alertData.policyWise || [];
  const crFail = proj.cloudRunFailures || [];
  const schFail = proj.schedulerFailures || [];
  const tskFail = proj.tasksFailures || [];
  const totalFailures = crFail.length + schFail.length + tskFail.length;
  const openCount = (alertData.openCount || 0) + totalFailures;

  return (
    <div className="report-section">
      <details open>
        <summary className="section-toggle">
          <h4>Alert Policies &amp; Incidents</h4>
          <span className="section-toggle-meta">
            {openCount === 0
              ? <span className="status-badge success">All healthy</span>
              : <span className="status-badge error">{openCount} issue{openCount > 1 ? 's' : ''}</span>}
          </span>
        </summary>

        {proj.alertPolicies && Object.keys(proj.alertPolicies).length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>CONFIGURED ALERT POLICIES</div>
            <div className="resource-table-wrap">
              <table className="resource-table">
                <thead><tr><th>Alert Name</th><th>Description</th><th>Severity</th></tr></thead>
                <tbody>
                  {Object.entries(proj.alertPolicies).map(([name, cfg]) => (
                    <tr key={name}>
                      <td style={{ fontWeight: 600 }}>{name}</td>
                      <td>{typeof cfg === 'object' ? cfg.description : cfg}</td>
                      <td><span className={`status-badge ${['ERROR', 'CRITICAL'].includes(typeof cfg === 'object' ? cfg.severity : '') ? 'error' : 'info'}`}>
                        {typeof cfg === 'object' ? cfg.severity : 'INFO'}
                      </span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>INCIDENT SUMMARY</div>
          <div className="resource-table-wrap">
            <table className="resource-table">
              <thead><tr><th>Policy Name</th><th>Total</th><th>Open</th><th>Closed</th><th>Status</th></tr></thead>
              <tbody>
                {policyWise.map((p, i) => (
                  <tr key={`policy-${i}`}>
                    <td style={{ fontWeight: 600 }}>{p.policyName}</td>
                    <td>{p.total}</td>
                    <td>{p.open}</td>
                    <td>{p.closed}</td>
                    <td><span className={`status-badge ${p.open === 0 ? 'success' : 'error'}`}>{p.open === 0 ? 'OK' : `ERROR - ${p.open} Active`}</span></td>
                  </tr>
                ))}
                <tr>
                  <td style={{ fontWeight: 600 }}>Cloud Run Jobs (Logging)</td>
                  <td>{crFail.length}</td>
                  <td>{crFail.length}</td>
                  <td>0</td>
                  <td><span className={`status-badge ${crFail.length === 0 ? 'success' : 'error'}`}>{crFail.length === 0 ? 'OK' : `ERROR - ${crFail.length} Failed`}</span></td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Cloud Scheduler (Logging)</td>
                  <td>{schFail.length}</td>
                  <td>{schFail.length}</td>
                  <td>0</td>
                  <td><span className={`status-badge ${schFail.length === 0 ? 'success' : 'error'}`}>{schFail.length === 0 ? 'OK' : `ERROR - ${schFail.length} Failed`}</span></td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Cloud Tasks (Logging)</td>
                  <td>{tskFail.length}</td>
                  <td>{tskFail.length}</td>
                  <td>0</td>
                  <td><span className={`status-badge ${tskFail.length === 0 ? 'success' : 'error'}`}>{tskFail.length === 0 ? 'OK' : `ERROR - ${tskFail.length} Failed`}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <ErrorSeverityTable proj={proj} policyWise={policyWise} />
      </details>
    </div>
  );
}

function ErrorSeverityTable({ proj, policyWise }) {
  const rows = [];
  const crFail = proj.cloudRunFailures || [];
  const schFail = proj.schedulerFailures || [];
  const tskFail = proj.tasksFailures || [];

  for (const p of policyWise) {
    if (['ERROR', 'CRITICAL'].includes(p.severity)) {
      if (p.incidents?.length) {
        for (const inc of p.incidents) {
          rows.push({ source: 'Alert Policy', name: p.policyName, severity: p.severity, state: 'Unhealthy', timestamp: inc.opened_at || 'N/A' });
        }
      } else {
        rows.push({ source: 'Alert Policy', name: p.policyName, severity: p.severity, state: 'Healthy', timestamp: 'No error severity happened in this time interval' });
      }
    }
  }

  if (crFail.length) crFail.forEach(f => rows.push({ source: 'Cloud Run Job', name: f.name, severity: 'ERROR', state: 'Unhealthy', timestamp: f.timestamp }));
  else rows.push({ source: 'Cloud Run Job', name: 'All Jobs', severity: 'ERROR', state: 'Healthy', timestamp: 'No error severity happened in this time interval' });

  if (schFail.length) schFail.forEach(f => rows.push({ source: 'Cloud Scheduler', name: f.name, severity: 'ERROR', state: 'Unhealthy', timestamp: f.timestamp }));
  else rows.push({ source: 'Cloud Scheduler', name: 'All Schedulers', severity: 'ERROR', state: 'Healthy', timestamp: 'No error severity happened in this time interval' });

  if (tskFail.length) tskFail.forEach(f => rows.push({ source: 'Cloud Tasks', name: f.name, severity: 'ERROR', state: 'Unhealthy', timestamp: f.timestamp }));
  else rows.push({ source: 'Cloud Tasks', name: 'All Tasks', severity: 'ERROR', state: 'Healthy', timestamp: 'No error severity happened in this time interval' });

  if (!rows.length) return null;

  const actualErrors = rows.filter(r => r.state !== 'Healthy').length;

  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>ERROR SEVERITY TIMESTAMPS</div>
      <details open={actualErrors > 0}>
        <summary className="section-toggle">
          <h4 style={{ fontSize: '0.78rem' }}>Error Details ({rows.length} entries)</h4>
          <span className="section-toggle-meta">
            {actualErrors === 0
              ? <span className="status-badge success">All clear</span>
              : <span className="status-badge error">{actualErrors} error{actualErrors > 1 ? 's' : ''}</span>}
          </span>
        </summary>
        <div className="resource-table-wrap">
          <table className="resource-table">
            <thead><tr><th>Source</th><th>Name</th><th>Severity</th><th>State</th><th>Timestamp (IST)</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.source}</td>
                  <td style={{ fontWeight: 600 }}>{r.name}</td>
                  <td><span className={`status-badge ${r.severity === 'CRITICAL' ? 'error' : 'warning'}`}>{r.severity}</span></td>
                  <td><span className={`status-badge ${r.state === 'Healthy' ? 'success' : 'error'}`}>{r.state}</span></td>
                  <td style={{ fontSize: '0.8rem' }}>{r.timestamp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="report-note info" style={{ marginTop: 8 }}>
          {actualErrors} error events found, {rows.length - actualErrors} categories with no errors
        </div>
      </details>
    </div>
  );
}

export default GcpAudit;
